import React from "react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { Trash2, Upload } from "lucide-react";

interface ProfilePictureProps {
  avatarUrl: string | null;
  username: string;
  onAvatarChange: (url: string | null) => void;
}

const ProfilePicture: React.FC<ProfilePictureProps> = ({
  avatarUrl,
  username,
  onAvatarChange,
}) => {
  const { t } = useTranslation();
  const { toast } = useToast();
  const fileInputRef = React.useRef<HTMLInputElement>(null);

  const uploadAvatar = async (event: React.ChangeEvent<HTMLInputElement>) => {
    try {
      if (!event.target.files || event.target.files.length === 0) {
        return;
      }

      const file = event.target.files[0];
      const fileSize = file.size / 1024 / 1024; // Convert to MB

      if (fileSize > 2) {
        toast({
          title: t("profile.uploadError"),
          description: t("profile.photoTooBig"),
          variant: "destructive",
        });
        return;
      }

      // Remove old avatar if exists
      if (avatarUrl) {
        const oldAvatarPath = avatarUrl.split("/").pop();
        if (oldAvatarPath) {
          await supabase.storage
            .from("profile_pictures")
            .remove([oldAvatarPath]);
        }
      }

      const fileExt = file.name.split(".").pop();
      const fileName = `${Math.random()}.${fileExt}`;
      const filePath = `${fileName}`;

      console.log("Uploading file:", filePath);

      const { error: uploadError, data } = await supabase.storage
        .from("profile_pictures")
        .upload(filePath, file, {
          cacheControl: "3600",
          upsert: false,
        });

      if (uploadError) {
        console.error("Upload error:", uploadError);
        throw uploadError;
      }

      console.log("Upload successful:", data);

      const { data: { publicUrl } } = supabase.storage
        .from("profile_pictures")
        .getPublicUrl(filePath);

      console.log("Public URL:", publicUrl);

      onAvatarChange(publicUrl);

      toast({
        title: t("profile.uploadSuccess"),
      });
    } catch (error) {
      console.error("Error uploading avatar:", error);
      toast({
        title: t("profile.uploadError"),
        variant: "destructive",
      });
    }
  };

  const removeAvatar = async () => {
    try {
      if (avatarUrl) {
        const avatarPath = avatarUrl.split("/").pop();
        if (avatarPath) {
          await supabase.storage
            .from("profile_pictures")
            .remove([avatarPath]);
        }
      }
      onAvatarChange(null);
      
      toast({
        title: t("profile.removePhoto"),
        description: t("common.success"),
      });
    } catch (error) {
      console.error("Error removing avatar:", error);
      toast({
        title: t("profile.uploadError"),
        variant: "destructive",
      });
    }
  };

  return (
    <div className="flex flex-col items-center gap-4">
      <Avatar className="w-24 h-24">
        <AvatarImage src={avatarUrl || undefined} alt={username} />
        <AvatarFallback>
          {username?.[0]?.toUpperCase() || "?"}
        </AvatarFallback>
      </Avatar>
      <div className="flex gap-2">
        <Button
          variant="outline"
          size="sm"
          onClick={() => fileInputRef.current?.click()}
        >
          <Upload className="w-4 h-4 mr-2" />
          {t("profile.uploadPhoto")}
        </Button>
        {avatarUrl && (
          <Button
            variant="outline"
            size="sm"
            onClick={removeAvatar}
          >
            <Trash2 className="w-4 h-4 mr-2" />
            {t("profile.removePhoto")}
          </Button>
        )}
        <input
          type="file"
          ref={fileInputRef}
          onChange={uploadAvatar}
          accept="image/*"
          className="hidden"
        />
      </div>
    </div>
  );
};

export default ProfilePicture;